import { useNavigate } from "react-router-dom";
import { Fragment, SyntheticEvent, useEffect, useState } from "react";

import moment from "moment";
import {
  Button,
  Chip,
  Container,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { visuallyHidden } from "@mui/utils";
import ApprovalOutlinedIcon from "@mui/icons-material/ApprovalOutlined";

import conf from "config-dlm";
import Refresh from "components/utils/Refresh";
import { useAuth } from "components/auth/AuthProvider";
import usePagination from "hooks/pagination";
import EmptyTableRow from "components/rows/Empty";
import SectionHeader from "components/utils/SectionHeader";
import ProtectedRoute from "components/auth/ProtectedRoute";
import LinearIndeterminate from "components/utils/LinearLoader";
import { a11yProps, CustomTabPanel } from "./Rows";
import { RequestSchema, useUserServiceRequests } from "hooks/user/base";
import {
  AxiosMutationRequest,
  usePavmApprove,
  usePavmRequestDetail,
  usePavmReject,
} from "hooks/pavm/requests";

export const PendingRow = (props: {
  request: RequestSchema;
  approve: AxiosMutationRequest;
  reject: AxiosMutationRequest;
}) => {
  const {
    request: { id, requester, subject, project, updatedAt, uri, status },
    approve,
    reject,
  } = props;

  const { user } = useAuth();
  const showEdit = user.role === "approver" || user.role === "admin";

  const navigate = useNavigate();
  const handleClick = () => navigate(`/requests/pending/${id}`);

  const [comment, setComment] = useState("");
  const [openDetail, setOpenDetail] = useState(false);

  const detail = usePavmRequestDetail(uri, id);

  const toggleDetail = () => setOpenDetail(!openDetail);
  const handleReject = () => reject.mutate({ uri, id, project, comment });
  const handleApprove = () => approve.mutate({ uri, id, project, comment });

  const chip = <Chip color="warning" label={status} onClick={handleClick} />;

  return (
    <Fragment>
      <TableRow key={id} sx={conf.LAST_CHILD_NO_BORDER}>
        <TableCell>
          <IconButton
            size="small"
            onClick={toggleDetail}
            aria-label="toggle detail row"
            sx={{
              transform: openDetail ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.3s",
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </TableCell>
        <TableCell>{chip}</TableCell>
        <TableCell align="right">{requester}</TableCell>
        <TableCell align="right">{subject}</TableCell>
        <TableCell align="right">
          <b>{project ?? "--"}</b>
        </TableCell>
        <TableCell align="right">{moment(updatedAt).fromNow()}</TableCell>
      </TableRow>

      {openDetail && (
        <TableRow key={`${id}-detail`} sx={conf.LAST_CHILD_NO_BORDER}>
          <TableCell colSpan={7}>
            <Box>
              <TextField
                multiline
                fullWidth
                aria-readonly
                margin="normal"
                InputProps={{ readOnly: true }}
                defaultValue={detail?.data?.text}
                sx={{
                  bgcolor: (theme) =>
                    theme.palette.mode === "light"
                      ? "primary.light"
                      : "primary.dark",
                  "&:last-child td, &:last-child th": { border: 0 },
                }}
              />

              {showEdit && (
                <TextField
                  size="small"
                  label="Comment (optional)"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  fullWidth
                  margin="normal"
                />
              )}

              {showEdit && (
                <Box my={1} textAlign="end">
                  <Button
                    size="small"
                    color="success"
                    variant="contained"
                    onClick={handleApprove}
                  >
                    Approve
                  </Button>

                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    onClick={handleReject}
                    style={{ marginLeft: "8px" }}
                  >
                    Reject
                  </Button>
                </Box>
              )}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
};

export const RequestsPending = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState(0);

  const paginate = usePagination({ defaultSort: "updatedAt" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const reject = usePavmReject();
  const approve = usePavmApprove();
  const pending = useUserServiceRequests({ status: "PENDING", ...body });

  const handleChange = (e: SyntheticEvent, value: number) => setValue(value);

  useEffect(() => {
    document.title = "GRACE | requests";
  }, []); // run once on mount

  return (
    <ProtectedRoute>
      <Container maxWidth="xl">
        <SectionHeader
          title="Requests"
          icon={<ApprovalOutlinedIcon />}
          buttons={[
            <TablePagination
              key={"requests-pending-pagination"}
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={pending?.data?.meta.total || 0}
              page={page}
              rowsPerPage={limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeLimit}
            />,
            <Refresh
              key={"requests-pending-refresh"}
              isFetching={pending.isFetching}
              onClick={pending.refetch}
            />,
          ]}
        />

        <Box sx={{ width: "100%" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              variant="fullWidth"
              onChange={handleChange}
              textColor="inherit"
              aria-label="request tabs"
              indicatorColor="secondary"
            >
              <Tab label="Pending" iconPosition="start" {...a11yProps(0)} />

              <Tab
                label="Closed"
                iconPosition="start"
                onClick={() => navigate("/requests/closed")}
                {...a11yProps(1)}
              />
            </Tabs>
          </Box>

          <CustomTabPanel value={value} index={0}>
            <TableContainer sx={{ boxShadow: 4, mb: 4, maxHeight: 582 }}>
              <Table aria-label="collapsible table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell />

                    {conf.COLUMNS.REQUESTS.PENDING.map((column) => (
                      <TableCell
                        key={column.key}
                        sortDirection={order}
                        align={column.isHeader ? "left" : "right"}
                      >
                        <TableSortLabel
                          active={sort === column.key}
                          direction={sort === column.key ? order : "asc"}
                          onClick={(event: React.MouseEvent<unknown>) =>
                            handleSort(event, column.key)
                          }
                        >
                          <b>{column.label}</b>
                          {sort === column.key ? (
                            <Box component="span" sx={visuallyHidden}>
                              {order === "desc"
                                ? "sorted descending"
                                : "sorted ascending"}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {pending.isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <LinearIndeterminate />
                      </TableCell>
                    </TableRow>
                  ) : pending.data?.data.length === 0 ? (
                    <EmptyTableRow colSpan={7} type="requests" />
                  ) : (
                    pending.data?.data?.map((request: RequestSchema) => (
                      <PendingRow
                        key={request.id}
                        reject={reject}
                        approve={approve}
                        request={request}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CustomTabPanel>
        </Box>
      </Container>
    </ProtectedRoute>
  );
};
